import { createFileRoute, Link } from '@tanstack/react-router';
import { useFamily } from '@/data/families';
import { useDancers } from '@/data/dancers';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/admin/families/$id')({
  component: FamilyDetail,
});

function FamilyDetail() {
  const { id } = Route.useParams();
  const familyQuery = useFamily(id);
  const dancersQuery = useDancers({ familyId: id });

  if (familyQuery.isLoading) {
    return <p aria-live="polite">Loading…</p>;
  }
  if (familyQuery.isError || !familyQuery.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        {familyQuery.error instanceof Error
          ? familyQuery.error.message
          : 'This family could not be loaded.'}
      </p>
    );
  }

  const f = familyQuery.data;
  const address = f.address as {
    line1: string;
    line2?: string;
    city: string;
    region: string;
    postalCode: string;
    country: string;
  };

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/admin/families"
            className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
          >
            ← All families
          </Link>
          <h1 className="mt-2">{f.primary_guardian_name}</h1>
          <p className="mt-1 text-[var(--color-ink-muted)]">{f.primary_guardian_email}</p>
        </div>
        <div className="flex gap-2">
          <Badge tone="accent">Active</Badge>
        </div>
      </div>

      <section aria-labelledby="contact-heading" className="grid gap-6 lg:grid-cols-2">
        <div className="surface-elevated p-6">
          <h2 id="contact-heading" className="font-medium text-[var(--text-lg)]">
            Contact
          </h2>
          <dl className="mt-4 grid grid-cols-[8rem_1fr] gap-y-2 text-[var(--text-sm)]">
            <dt className="text-[var(--color-ink-muted)]">Phone</dt>
            <dd className="tabular-nums">{f.primary_guardian_phone}</dd>
            <dt className="text-[var(--color-ink-muted)]">Billing email</dt>
            <dd>{f.billing_email}</dd>
            <dt className="text-[var(--color-ink-muted)]">Stripe customer</dt>
            <dd className="font-mono text-[var(--color-ink-muted)] text-[var(--text-xs)]">
              {f.stripe_customer_id ?? 'Not yet created'}
            </dd>
          </dl>
        </div>

        <div className="surface-elevated p-6">
          <h2 className="font-medium text-[var(--text-lg)]">Address</h2>
          <address className="mt-4 leading-[var(--leading-relaxed)] text-[var(--text-sm)] not-italic">
            {address.line1}
            {address.line2 ? <>, {address.line2}</> : null}
            <br />
            {address.city}, {address.region} {address.postalCode}
            <br />
            {address.country}
          </address>
        </div>
      </section>

      <section aria-labelledby="dancers-heading">
        <h2 id="dancers-heading" className="text-[var(--text-xl)]">
          Dancers
        </h2>
        {dancersQuery.isLoading && <p className="mt-2 text-[var(--color-ink-subtle)]">Loading…</p>}
        {dancersQuery.data?.rows.length === 0 && (
          <p className="mt-2 text-[var(--color-ink-muted)]">No dancers on this family yet.</p>
        )}
        {dancersQuery.data && dancersQuery.data.rows.length > 0 && (
          <ul className="mt-4 grid gap-2">
            {dancersQuery.data.rows.map((d) => (
              <li key={d.id} className="surface flex items-center justify-between p-4">
                <div>
                  <p className="font-medium">
                    {d.preferred_name ?? d.first_name} {d.last_name}
                  </p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    Born {d.date_of_birth}
                    {d.pronouns ? ` · ${d.pronouns}` : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge tone={d.status === 'active' ? 'success' : 'neutral'}>{d.status}</Badge>
                  {d.costume_due && <Badge tone="warn">costume due</Badge>}
                  {d.payment_due && <Badge tone="danger">payment due</Badge>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
        Family ID <span className="font-mono">{f.id}</span> · Created {f.created_at.slice(0, 10)}
      </p>
    </div>
  );
}
